import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { detectDevice, getStoreUrl } from "@/lib/device-detection"
import { QRCodeCard } from "./qr-code-card"
import { ParticleTextEffect } from "./particle-text-effect"

interface HeroSectionProps {
  t: Record<string, any>
}

export function HeroSection({ t }: HeroSectionProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [isDesktop, setIsDesktop] = useState(true)
  const [device, setDevice] = useState<"android" | "ios" | "desktop">("desktop")
  const [shouldAutoRedirect, setShouldAutoRedirect] = useState(false)

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth
      setIsMobile(width < 768)
      setIsDesktop(width >= 1024)
    }

    checkDevice()
    window.addEventListener("resize", checkDevice)
    return () => window.removeEventListener("resize", checkDevice)
  }, [])

  useEffect(() => {
    const detectedDevice = detectDevice()
    setDevice(detectedDevice)

    // Auto-redirect mobile users to appropriate app store after 2 seconds
    if (detectedDevice !== "desktop") {
      const timer = setTimeout(() => {
        const storeUrl = getStoreUrl(detectedDevice)
        window.location.href = storeUrl
      }, 2000)

      setShouldAutoRedirect(true)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleDownloadClick = (type: "ios" | "android") => {
    const url = getStoreUrl(type)
    window.open(url, "_blank")
  }

  const androidUrl =
    "https://play.google.com/store/apps/details?id=ru.taximaster.tmtaxicaller.id3052&pcampaignid=web_share"
  const iosUrl = "https://apps.apple.com/uz/app/cardin-taxi/id6670791817"

  // Aniq Apple icon
  const AppleIcon = () => (
    <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )

  // Aniq Google Play icon
  const PlayIcon = () => (
    <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
    </svg>
  )

  return (
    <section className="relative overflow-hidden min-h-screen flex flex-col justify-between py-20 px-4 md:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Radiating lines background */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="dots" x="40" y="40" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1" fill="rgba(59, 130, 246, 0.3)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />

          {/* Radiating lines */}
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * Math.PI * 2
            const x1 = 50
            const y1 = 50
            const x2 = 50 + Math.cos(angle) * 100
            const y2 = 50 + Math.sin(angle) * 100
            return (
              <line
                key={i}
                x1={`${x1}%`}
                y1={`${y1}%`}
                x2={`${x2}%`}
                y2={`${y2}%`}
                stroke="rgba(59, 130, 246, 0.2)"
                strokeWidth="0.5"
                strokeDasharray="2,2"
                opacity="0.5"
              />
            )
          })}
        </svg>

        {/* Animated radiate circles */}
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-blue-500/30 animate-radiate"
            style={{
              width: `${200 + i * 150}px`,
              height: `${200 + i * 150}px`,
              animationDelay: `${i * 0.6}s`,
            }}
          />
        ))}
      </div>

      <div className="flex-1 flex items-start justify-center pt-8 md:pt-12 lg:pt-16">
        <ParticleTextEffect words={["CARDIN", "TAXI", "SMART", "RIDE"]} />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10 flex-1 flex flex-col justify-center">
        {/* Hero Content */}
        <motion.div
          className="text-center mb-12 md:mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h3
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6"
            style={{ marginTop: isMobile ? '120px' : '0px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t?.hero?.title || "Tez va Ishonchli Taksi Xizmati"}
          </motion.h3>

          <motion.p
            className="text-base md:text-lg lg:text-xl text-gray-300 mb-12 md:mb-16 max-w-3xl mx-auto text-balance leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t?.hero?.subtitle || "Shahar bo'ylab ishonchli sayohatlar"}
          </motion.p>

          {/* Download Buttons - Yaxshilangan dizayn */}
          <motion.div
            className="flex flex-row gap-4 md:gap-6 justify-center mb-12 md:mb-16 items-center flex-wrap"
            style={{ marginTop: isMobile ? '100px' : '220px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* iOS Button - App Store dizayni */}
            <motion.button
              onClick={() => handleDownloadClick("ios")}
              className="group relative px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 md:py-4.5 bg-black hover:bg-gray-900 text-white rounded-xl md:rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 border border-gray-800 hover:border-gray-700 min-w-[160px] sm:min-w-[180px]"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <AppleIcon />
              <div className="flex flex-col items-start">
                <span className="text-[10px] sm:text-xs text-gray-400 leading-none mb-0.5">
                  Download on the
                </span>
                <span className="text-sm sm:text-base md:text-lg leading-none font-bold">
                  App Store
                </span>
              </div>
            </motion.button>

            {/* Android Button - Google Play dizayni */}
            <motion.button
              onClick={() => handleDownloadClick("android")}
              className="group relative px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 md:py-4.5 bg-black hover:bg-gray-900 text-white rounded-xl md:rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30 border border-gray-800 hover:border-gray-700 min-w-[160px] sm:min-w-[180px]"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <PlayIcon />
              <div className="flex flex-col items-start">
                <span className="text-[10px] sm:text-xs text-gray-400 leading-none mb-0.5">
                  GET IT ON
                </span>
                <span className="text-sm sm:text-base md:text-lg leading-none font-bold">
                  Google Play
                </span>
              </div>
            </motion.button>
          </motion.div>

          {/* Auto-redirect indicator for mobile */}
          {shouldAutoRedirect && (
            <motion.div
              className="mb-8 text-sm text-gray-400 animate-pulse flex items-center justify-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {device === "ios" ? "App Store'ga yo'naltirilmoqda..." : "Play Market'ga yo'naltirilmoqda..."}
            </motion.div>
          )}
        </motion.div>

        {/* QR Codes for Desktop */}
        {isDesktop && (
          <motion.div
            className="mt-20 pt-12 border-t border-gray-700/50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-center text-gray-400 mb-8 text-sm md:text-base">
              {t?.buttons?.scanQR || "Telefoningiz bilan QR kodni skanerlang"}
            </p>
            <div className="flex justify-center gap-8 md:gap-12 lg:gap-16 flex-wrap">
              <QRCodeCard url={iosUrl} label="iOS - App Store" platform="ios" />
              <QRCodeCard url={androidUrl} label="Android - Google Play" platform="android" />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  )
}